import { useEffect, useState } from 'react'
import {
  AlertTime,
  deleteRecipe,
  duplicateRecipe,
  getRecipeExtraData,
  insertRecipe,
  recipeChanged,
  setRecipeMode,
  updateRecipe,
  type recipe,
} from '../../../utils/recipeUtils'
import './RecipePopUpContent.scss'
import { getMessage } from '../../../utils/messageUtils'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import CollapsibleSection from '../../CollapsibleSection'
import RecipeMain from './RecipeMain'
import RecipeStepList from './Steps/RecipeStepList'
import RecipeIngredientList from './Ingredients/RecipeIngredientList'
import type { ingredient } from '../../../utils/ingredientUtils'
import PopupError from '../../PopupForm/PopupError'
import Spinner from '../../Spinner/Spinner'
import { completeRecipe } from '../../../utils/storageUtils'
import type { Mode } from '../../../App'
import RecipeLinkList from './Links/RecipeLinkList'
import type { User } from '../../../utils/loginUtils'

interface RecipePopupContentProps {
  recipe: recipe | null
  currentTypes: string[]
  recipeList: recipe[] | undefined
  user: User
  existingIngredients: ingredient[]
  isMobile: boolean
  setPopupOnOk: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
  closePopup: () => void
  reLoadData: () => void
  setPopupExtraButtons: React.Dispatch<
    React.SetStateAction<ExtraButton[] | undefined>
  >
  setMode: (mode: Mode) => void
}

function RecipePopUpContent({
  recipe,
  currentTypes,
  recipeList,
  user,
  existingIngredients,
  isMobile,
  setPopupOnOk,
  closePopup,
  reLoadData,
  setPopupExtraButtons,
  setMode,
}: RecipePopupContentProps) {
  const [name, setName] = useState(recipe?.name || '')
  const [type, setType] = useState(recipe?.type || '')
  const [time, setTime] = useState(recipe?.time || 0)
  const [tuppers, setTuppers] = useState(recipe?.tuppers || 0)
  const [alert, setAlert] = useState(recipe?.alert || '')
  const [alertTime, setAlertTime] = useState<AlertTime>(
    recipe?.alertTime || AlertTime.SAME_DAY_MORNING
  )
  const [desayuno, setDesayuno] = useState(recipe?.desayuno === 1)
  const [almuerzo, setAlmuerzo] = useState(recipe?.almuerzo === 1)
  const [comida, setComida] = useState(recipe?.comida === 1)
  const [merienda, setMerienda] = useState(recipe?.merienda === 1)
  const [cena, setCena] = useState(recipe?.cena === 1)
  const [recipeIngredients, setRecipeIngredients] = useState(
    recipe?.ingredients || []
  )
  const [steps, setSteps] = useState(recipe?.steps || [])
  const [recipeLinks, setRecipeLinks] = useState(recipe?.links || [])

  const [errorMessage, setErrorMessage] = useState('')

  // Popup
  const [extraPopupMessage, setExtraPopupMessage] = useState('')
  const [extraPopupOnOk, setExtraPopupOnOk] = useState<
    (() => void) | undefined
  >(undefined)
  const [extraPopupOpen, setExtraPopupOpen] = useState(false)

  // Collapsible sections state - only one can be open at a time
  const [openSection, setOpenSection] = useState<string>('general')

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const fetchRecipeExtraData = async () => {
      // If the steps have already been set do nothing
      if (!recipe || !recipe.id || steps.length !== 0) return

      setIsLoading(true)
      const data = await getRecipeExtraData(user.id!, recipe.id)
      setIsLoading(false)

      if (data.success && data.steps) {
        setSteps(data.steps)
      }
      if (data.success && data.ingredients) {
        setRecipeIngredients(data.ingredients)
      }
      if (data.success && data.links) {
        setRecipeLinks(data.links)
      }
    }

    fetchRecipeExtraData()
  }, [recipe, user.id, steps.length])

  const checkIngredientInputs = () => {
    const ingredientsToCheck = recipeIngredients.filter(i => !i.deleted)
    if (ingredientsToCheck.find(i => !i.productId)) {
      setErrorMessage(getMessage('recipe_ingredient_empty'))
      return false
    }
    if (ingredientsToCheck.find(i => !i.quantity || i.quantity <= 0)) {
      setErrorMessage(getMessage('recipe_ingredient_qty_invalid'))
      return false
    }
    if (
      new Set(ingredientsToCheck.map(i => i.productId)).size !==
      ingredientsToCheck.length
    ) {
      setErrorMessage(getMessage('recipe_ingredient_duplicated'))
      return false
    }
    return true
  }

  const checkStepInputs = () => {
    const stepsToCheck = steps.filter(s => !s.deleted)
    if (stepsToCheck.find(s => s.step.trim() === '')) {
      setErrorMessage(getMessage('recipe_step_empty'))
      return false
    }
    if (stepsToCheck.find(s => !s.step_number)) {
      setErrorMessage(getMessage('recipe_step_empty'))
      return false
    }
    if (
      new Set(stepsToCheck.map(s => s.step_number)).size !== stepsToCheck.length
    ) {
      setErrorMessage(getMessage('recipe_step_num_duplicated'))
      return false
    }
    return true
  }

  const checkLinkInputs = () => {
    const linksToCheck = recipeLinks.filter(l => !l.deleted)
    if (linksToCheck.find(l => l.url.trim() === '')) {
      setErrorMessage(getMessage('recipe_link_empty'))
      return false
    }
    return true
  }

  const checkRecipeInputs = () => {
    if (
      recipeList?.find(
        r => r.id !== recipe?.id && r.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      setErrorMessage(getMessage('recipe_exists'))
      return false
    }
    if (name.trim() === '') {
      setErrorMessage(getMessage('recipe_name_empty'))
      return false
    }
    if (!desayuno && !almuerzo && !comida && !merienda && !cena) {
      setErrorMessage(getMessage('recipe_time_empty'))
      return false
    }
    return checkStepInputs() && checkIngredientInputs() && checkLinkInputs()
  }

  const saveNewRecipe = async () => {
    // Create the new recipe and send it to backend
    const newRecipe: recipe = {
      name: name.trim(),
      type: type.trim() !== '' ? type.trim() : null,
      tuppers,
      alert: alert.trim() !== '' ? alert.trim() : null,
      alertTime: alert.trim() !== '' ? alertTime : null,
      desayuno: desayuno ? 1 : 0,
      almuerzo: almuerzo ? 1 : 0,
      comida: comida ? 1 : 0,
      merienda: merienda ? 1 : 0,
      cena: cena ? 1 : 0,
      time,
      steps,
      ingredients: recipeIngredients,
      links: recipeLinks,
    }
    return (await insertRecipe(user.id!, newRecipe)) as {
      success: boolean
      error?: string
    }
  }

  const saveRecipe = async () => {
    // Checks
    if (!checkRecipeInputs()) return

    setIsLoading(true)
    let result: { success: boolean; error?: string } | undefined
    if (!recipe) {
      result = await saveNewRecipe()
    } else {
      result = await updateExistingRecipe()
    }
    setIsLoading(false)
    if (result === undefined) {
      // No changes
      closePopup()
      return
    }
    if (!result.success) {
      setErrorMessage(getMessage('recipe_save_error') + (result.error || ''))
    } else {
      reLoadData()
      closePopup()
    }
  }
  setPopupOnOk(() => saveRecipe)

  const updateExistingRecipe = async () => {
    const newRecipe: recipe = { ...recipe! }
    newRecipe.name = name.trim()
    newRecipe.type = type.trim() !== '' ? type.trim() : null
    newRecipe.tuppers = tuppers
    newRecipe.alert = alert.trim() !== '' ? alert.trim() : null
    newRecipe.alertTime = alert.trim() !== '' ? alertTime : null
    newRecipe.desayuno = desayuno ? 1 : 0
    newRecipe.almuerzo = almuerzo ? 1 : 0
    newRecipe.comida = comida ? 1 : 0
    newRecipe.merienda = merienda ? 1 : 0
    newRecipe.cena = cena ? 1 : 0
    newRecipe.time = time
    newRecipe.steps = steps
    newRecipe.ingredients = recipeIngredients
    newRecipe.links = recipeLinks

    if (recipeChanged(recipe!, newRecipe)) {
      return (await updateRecipe(user.id!, newRecipe)) as {
        success: boolean
        error?: string
      }
    } else {
      return undefined
    }
  }

  const completeExistingRecipe = async () => {
    if (!recipe || !recipe.id) return
    setIsLoading(true)
    const result = (await completeRecipe(user.id!, recipe.id, tuppers)) as {
      success: boolean
      error?: string
    }
    setIsLoading(false)
    if (!result.success) {
      setErrorMessage(getMessage('recipe_save_error') + (result.error || ''))
    }
    setExtraPopupOpen(false)
    closePopup()
  }

  const clickDeleteRecipe = () => {
    setExtraPopupMessage(getMessage('recipe_delete_confirm'))
    setExtraPopupOnOk(() => () => deleteExistingRecipe(recipe!))
    setExtraPopupOpen(true)
  }

  const clickCompleteRecipe = () => {
    setExtraPopupMessage(
      getMessage('recipe_complete_confirm', [tuppers.toString()])
    )
    setExtraPopupOnOk(() => () => completeExistingRecipe())
    setExtraPopupOpen(true)
  }

  const clickRecipeMode = () => {
    setRecipeMode(user.id!, recipe, recipeIngredients, steps, setMode)
  }

  const deleteExistingRecipe = async (recipeToDelete: recipe) => {
    setIsLoading(true)
    const result = (await deleteRecipe(user.id!, recipeToDelete.id!)) as {
      success: boolean
      error?: string
    }
    setIsLoading(false)
    if (!result.success) {
      setErrorMessage(getMessage('recipe_delete_error') + (result.error || ''))
    } else {
      reLoadData()
      closePopup()
    }
    setExtraPopupOpen(false)
  }

  const clickDuplicateRecipe = () => {
    if (!recipe) return

    setExtraPopupMessage(
      getMessage('recipe_duplicate_confirm', [tuppers.toString()])
    )
    setExtraPopupOnOk(() => () => duplicateExistingRecipe())
    setExtraPopupOpen(true)
  }

  const duplicateExistingRecipe = async () => {
    if (!recipe) return
    setIsLoading(true)
    const result = (await duplicateRecipe(user.id!, recipe.id!)) as {
      success: boolean
      error?: string
    }
    setIsLoading(false)
    if (!result.success) {
      setErrorMessage(
        getMessage('recipe_duplicate_error') + (result.error || '')
      )
    } else {
      reLoadData()
      closePopup()
    }
    setExtraPopupOpen(false)
  }

  let extraButtons = [] as ExtraButton[]
  if (recipe) {
    extraButtons = [
      {
        label: getMessage('recipe_cook'),
        className: 'recipe-cook-button',
        onClick: clickRecipeMode,
      },
      {
        label: getMessage('complete'),
        className: 'recipe-complete-button',
        onClick: clickCompleteRecipe,
      },
      {
        label: getMessage('duplicate'),
        className: 'recipe-duplicate-button',
        onClick: clickDuplicateRecipe,
      },
      {
        label: getMessage('delete'),
        className: 'popup-delete-button',
        onClick: recipe ? () => clickDeleteRecipe() : undefined,
      },
    ] as ExtraButton[]
  }
  setPopupExtraButtons(extraButtons)

  return (
    <div
      className={`recipe-pop-up-content ${isMobile ? 'recipe-pop-up-content-mobile' : ''}`}
    >
      <PopupError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <CollapsibleSection
        title={getMessage('general_content')}
        isOpen={openSection === 'general'}
        onToggle={() =>
          setOpenSection(openSection === 'general' ? '' : 'general')
        }
        isMobile={isMobile}
      >
        <RecipeMain
          name={name}
          setName={setName}
          type={type}
          setType={setType}
          time={time}
          setTime={setTime}
          tuppers={tuppers}
          setTuppers={setTuppers}
          alert={alert}
          setAlert={setAlert}
          alertTime={alertTime}
          setAlertTime={setAlertTime}
          desayuno={desayuno}
          setDesayuno={setDesayuno}
          almuerzo={almuerzo}
          setAlmuerzo={setAlmuerzo}
          comida={comida}
          setComida={setComida}
          merienda={merienda}
          setMerienda={setMerienda}
          cena={cena}
          setCena={setCena}
          currentTypes={currentTypes}
          isMobile={isMobile}
          user={user}
        />
      </CollapsibleSection>
      <CollapsibleSection
        title={getMessage('ingredients')}
        isOpen={openSection === 'ingredients'}
        onToggle={() =>
          setOpenSection(openSection === 'ingredients' ? '' : 'ingredients')
        }
        isMobile={isMobile}
      >
        <RecipeIngredientList
          existingIngredients={existingIngredients}
          recipeIngredients={recipeIngredients}
          setRecipeIngredients={setRecipeIngredients}
        />
      </CollapsibleSection>
      <CollapsibleSection
        title={getMessage('steps')}
        isOpen={openSection === 'steps'}
        onToggle={() => setOpenSection(openSection === 'steps' ? '' : 'steps')}
        isMobile={isMobile}
      >
        <RecipeStepList steps={steps} setSteps={setSteps} />
      </CollapsibleSection>
      <CollapsibleSection
        title={getMessage('links')}
        isOpen={openSection === 'links'}
        onToggle={() => setOpenSection(openSection === 'links' ? '' : 'links')}
        isMobile={isMobile}
      >
        <RecipeLinkList links={recipeLinks} setLinks={setRecipeLinks} />
      </CollapsibleSection>
      <Popup
        isOpen={extraPopupOpen}
        isMobile={isMobile}
        message={extraPopupMessage}
        onOk={extraPopupOnOk}
        onClose={() => setExtraPopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default RecipePopUpContent
